import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { get } from 'lodash-es';
import { InventoryEntities } from 'client/data/models/inventory';
import { DEFAULT_PRICE, DEAL_TYPE_ICON, TEXT_COLOR } from 'client/site-modules/shared/components/deal/deal-constants';
import { isDisplayBelowMarketCopy } from 'site-modules/shared/utils/inventory/usurp-inventory-card';
import { getSavingsText } from 'client/site-modules/shared/utils/inventory-utils/get-savings-text';
import { PriceLabel } from 'site-modules/shared/components/usurp-inventory-card/price-label/price-label';
import { resolveDisplayPrice } from 'site-modules/shared/utils/inventory-utils/resolve-display-price';
import { isDealTypeAvailable } from 'site-modules/shared/utils/inventory-utils/is-deal-type-available';
import './pricing-details-srp.scss';

export function PricingDetails({
  vehicle,
  displayLeaseMonthlyPayment = false,
  displayLoanMonthlyPayment = false,
  displayMSRP = false,
  includeDeliveryFee = false,
  dealerFeesLink = null,
}) {
  const { prices, type } = vehicle;

  const priceToShow = resolveDisplayPrice({ prices, displayLeaseMonthlyPayment, displayLoanMonthlyPayment });
  const deliveryFee = vehicle?.deliveryType === 'Free' ? 0 : (vehicle?.deliveryFee ?? 0);
  const savings = get(vehicle, 'computedDisplayInfo.priceValidation.priceDiscount', 0);
  const displayBelowMarketCopy = isDisplayBelowMarketCopy({
    hasMonthlyPayment: displayLeaseMonthlyPayment || displayLoanMonthlyPayment,
    discountPriceIsCompliant: get(prices, 'discountPriceIsCompliant', false),
    savings,
  });

  const dealType = get(vehicle, 'computedDisplayInfo.priceValidation.dealType', '');
  const isDealTypeBadgeEnabled = isDealTypeAvailable({ dealType, prices, inventoryType: type });

  if (!priceToShow) return <div className="text-gray-dark heading-3">{DEFAULT_PRICE}</div>;

  return (
    <div className="pricing-details d-flex flex-column">
      <PriceLabel
        price={priceToShow}
        prices={prices}
        displayMSRP={displayMSRP}
        displayLeaseMonthlyPayment={displayLeaseMonthlyPayment}
        displayLoanMonthlyPayment={displayLoanMonthlyPayment}
        includeDeliveryFee={includeDeliveryFee}
        deliveryFee={deliveryFee}
        dealerFeesLink={dealerFeesLink}
      />
      {isDealTypeBadgeEnabled && (
        <div>
          <span className="fw-bold text-gray-darkest">
            <i
              className={classnames('align-middle me-0_75', DEAL_TYPE_ICON[dealType], TEXT_COLOR[dealType])}
              style={{ width: '1em', height: '1em', display: 'inline-block' }}
              aria-hidden
            />
            {dealType.toLowerCase()} price
          </span>

          {displayBelowMarketCopy && <div className="size-12">{getSavingsText(savings).toLowerCase()}</div>}
        </div>
      )}
    </div>
  );
}

PricingDetails.propTypes = {
  vehicle: InventoryEntities.InventoryVin.isRequired,
  displayLeaseMonthlyPayment: PropTypes.bool,
  displayLoanMonthlyPayment: PropTypes.bool,
  displayMSRP: PropTypes.bool,
  includeDeliveryFee: PropTypes.bool,
  dealerFeesLink: PropTypes.node,
};
